// Account credentials stay on the main API; only scoped tickets reach the media server.
export const MAX_FILE_SIZE = 2 * 1024 ** 3;
export const stopped = () => Object.assign(new Error('Загрузка приостановлена. Можно продолжить.'), {name: 'AbortError'});
export function checkSignal(signal) { if (signal?.aborted) throw stopped(); }
function error(message, status = 0) { return Object.assign(new Error(message), {status}); }
function messageOf(data, status) {
  const detail = data?.detail || data?.message || data?.error || data?.errors;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.filter(x => typeof x === 'string').join(' ') || `Ошибка загрузки (HTTP ${status}).`;
  if (status === 401) return 'Сессия истекла. Войдите в аккаунт и продолжите ту же загрузку.';
  if (status === 413) return 'Сервер отклонил размер запроса. Передайте менеджеру номер загрузки.';
  if (status === 507) return 'В хранилище недостаточно свободного места. Переданные части сохранены; сообщите менеджеру.';
  return status === 503 ? 'Хранилище временно недоступно. Переданные части сохранены; можно продолжить позже.' : `Не удалось выполнить запрос (HTTP ${status}).`;
}
export function safeMediaURL(value) {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password) throw error('Сервер вернул небезопасную ссылку.');
  return url;
}
export async function requestJSON(url, options = {}, signal) {
  checkSignal(signal);
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, {once: true});
  const timer = setTimeout(abort, 120000);
  try {
    const response = await fetch(url, {...options, signal: controller.signal,
      credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer'});
    let data;
    try { data = await response.json(); }
    catch { throw error(`Сервер вернул ответ вместо данных (HTTP ${response.status}).`, response.ok ? 502 : response.status); }
    if (!response.ok || data?.status === 'error') throw Object.assign(error(messageOf(data, response.status), Number(data?.code) || (response.ok ? 400 : response.status)), {code: data?.code});
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw error('Некорректный ответ сервера.', 502);
    return data;
  } catch (failure) {
    checkSignal(signal);
    if (failure.name === 'AbortError') throw error('Сервер не ответил вовремя. Повторите запрос.');
    throw failure;
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}

// An image/fetch redirect between API and home changes the request Origin in
// browsers. Resolve the authenticated main broker first, then request the
// original directly. Ordinary download navigation keeps its access recheck.
export async function resolveMediaURL(apiBase, value, {preview = false, signal} = {}) {
  checkSignal(signal);
  const url = safeMediaURL(value);
  if (preview && url.origin === new URL(apiBase).origin && url.pathname.endsWith('/materials-content/')) {
    url.searchParams.set('resolve', '1');
    const result = await requestJSON(url.href, {cache: 'no-store'}, signal);
    checkSignal(signal);
    return safeMediaURL(result.url).href;
  }
  return url.href;
}
function pause(ms, signal) {
  return new Promise((resolve, reject) => {
    checkSignal(signal);
    const done = () => { signal?.removeEventListener('abort', abort); resolve(); };
    const timer = setTimeout(done, ms);
    const abort = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); reject(stopped()); };
    signal?.addEventListener('abort', abort, {once: true});
  });
}
const retryable = status => !status || [408, 425, 429, 500, 502, 503, 504].includes(status);
async function retry(fn, signal, onRetry) {
  for (let attempt = 0; ; attempt++) {
    try { return await fn(); }
    catch (failure) {
      checkSignal(signal);
      if (attempt >= 6 || !retryable(failure.status)) throw failure;
      onRetry?.({attempt: attempt + 1, message: 'Проверяем соединение с хранилищем…'});
      await pause(Math.min(1000 * 2 ** attempt, 10000), signal);
    }
  }
}
async function readPart(blob) {
  if (blob.arrayBuffer) return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(error('Не удалось прочитать файл. Сохраните его на устройство и выберите заново.'));
    reader.readAsArrayBuffer(blob);
  });
}
const fingerprintCache = new WeakMap();
export async function contentFingerprint(file, prefix = '', signal, onProgress) {
  checkSignal(signal);
  if (!file.size || file.size > MAX_FILE_SIZE) throw error('Файл должен быть непустым и не больше 2 ГиБ.');
  if (!window.crypto?.subtle) throw error('Для загрузки нужен HTTPS и актуальный браузер.');
  const cached = fingerprintCache.get(file)?.get(prefix);
  if (cached) { onProgress?.(100); return cached; }
  const hashes = [];
  for (let at = 0; at < file.size; at += 1024 * 1024) {
    checkSignal(signal);
    const end = Math.min(file.size, at + 1024 * 1024);
    const bytes = await readPart(file.slice(at, end));
    hashes.push(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)));
    onProgress?.(Math.floor(end * 100 / file.size));
  }
  checkSignal(signal);
  const metadata = new TextEncoder().encode(JSON.stringify([prefix, file.name, file.size]));
  const bytes = new Uint8Array(metadata.length + hashes.length * 32);
  bytes.set(metadata);
  hashes.forEach((hash, i) => bytes.set(hash, metadata.length + i * 32));
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)).slice(0, 16);
  digest[6] = (digest[6] & 15) | 64;
  digest[8] = (digest[8] & 63) | 128;
  const hex = Array.from(digest, n => n.toString(16).padStart(2, '0')).join('');
  const fingerprint = `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
  if (!fingerprintCache.has(file)) fingerprintCache.set(file, new Map());
  fingerprintCache.get(file).set(prefix, fingerprint);
  return fingerprint;
}
const requestIds = new Map();
export function resumableRequestId(fingerprint) {
  const key = `home-media-request:${fingerprint}`;
  let saved = requestIds.get(key);
  if (!saved) { try { saved = JSON.parse(localStorage.getItem(key)); } catch { /* память вкладки */ } }
  const age = Date.now() - saved?.created;
  if (saved && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(saved.id) && age >= 0 && age < 47 * 3600000) return saved.id;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, n => n.toString(16).padStart(2, '0')).join('');
  saved = {id: `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`, created: Date.now()};
  requestIds.set(key, saved);
  try { localStorage.setItem(key, JSON.stringify(saved)); } catch { /* продолжение в этой вкладке */ }
  return saved.id;
}
export function forgetResumableRequestId(fingerprint) {
  requestIds.delete(`home-media-request:${fingerprint}`);
  try { localStorage.removeItem(`home-media-request:${fingerprint}`); } catch { /* Memory-only browser. */ }
}
// Only the main API may authorize a fallback. Account credentials never reach
// URLs supplied by the home server or an unrelated origin.
function fallbackURL(base, descriptor, action, uploadId) {
  const api = new URL(base);
  const url = new URL(descriptor?.[action + '_url'] || '', api);
  const expected = `/main/api/media/direct/${uploadId}/fallback/${action}/`;
  if (!descriptor?.[action + '_url'] || url.origin !== api.origin || url.username || url.password ||
      !url.pathname.endsWith(expected) || url.search || url.hash) {
    throw error('Основной сервер не подтвердил резервную загрузку. Обновите страницу.', 502);
  }
  return url;
}
export async function uploadDirectFile(base, {file, token, initPath, initData, completePath, onProgress, onRetry, onPrepared, prepared, signal}) {
  if (!token) throw error('Войдите в аккаунт и повторите загрузку.', 401);
  if (!file.size || file.size > MAX_FILE_SIZE) throw error('Файл должен быть непустым и не больше 2 ГиБ.');
  const mainHeaders = {Authorization: `Bearer ${token}`};
  const control = (path, data) => requestJSON(base + path, {method: 'POST',
    headers: {...mainHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify(data)}, signal);
  let init, offset, origin, uploadId, useFallback = false, committed = false;
  const validateOffset = value => {
    if (!Number.isSafeInteger(value) || value < 0 || value > file.size) throw error('Неверное подтверждение размера файла.', 502);
    return value;
  };
  const report = () => onProgress?.({loaded: offset, total: file.size, percent: Math.min(99, Math.floor(offset * 100 / file.size)),
    transport: useFallback ? 'main-fallback' : 'direct'});
  async function renew(initialDescriptor) {
    const next = initialDescriptor || await retry(() => control(initPath, initData), signal, onRetry);
    if (!next.upload_id || !/^[0-9a-f-]{36}$/i.test(next.upload_id)) throw error('Не указан идентификатор загрузки.', 502);
    const expectedId = initData.upload_id || initData.request_id;
    if ((expectedId && expectedId !== next.upload_id) || (uploadId && uploadId !== next.upload_id)) throw error('Изменился идентификатор загрузки.', 502);
    uploadId = next.upload_id;
    // A main-server commit is authoritative and must not depend on home being online again.
    if (next.main_committed === true) {
      offset = validateOffset(next.received_bytes);
      if (offset !== file.size) throw error('Сервер подтвердил неполный файл.', 502);
      init = next; committed = true;
      onPrepared?.({upload_id: uploadId}); report(); return;
    }
    if (next.fallback) ['chunk', 'finish', 'status'].forEach(action => fallbackURL(base, next.fallback, action, uploadId));
    if (next.transport === 'main-fallback') useFallback = true;
    if (!useFallback) {
      if (!next.direct || !next.direct.token) throw error('Сервер ещё не готов к прямой загрузке. Обновите сервер и страницу.', 400);
      const urls = ['chunk', 'finish', 'status'].map(action => safeMediaURL(next.direct[action + '_url']));
      const nextOrigin = urls[0].origin;
      if (urls.some(url => url.origin !== nextOrigin) || (origin && origin !== nextOrigin)) throw error('Изменился адрес хранилища.', 502);
      if (!Number.isFinite(next.direct.expires_at)) throw error('Не указан срок разрешения загрузки.', 502);
      origin = nextOrigin;
    }
    init = next;
    offset = validateOffset(next.received_bytes ?? 0);
    onPrepared?.({upload_id: uploadId});
    report();
  }
  const nearExpiry = () => !committed && !useFallback && Date.now() / 1000 > init.direct.expires_at - 60;
  const transfer = (action, method, body) => {
    const url = useFallback ? fallbackURL(base, init.fallback, action, uploadId) : safeMediaURL(init.direct[action + '_url']);
    if (!useFallback && url.origin !== origin) throw error('Неверный адрес хранилища.', 502);
    if (action === 'chunk') url.searchParams.set('offset', String(offset));
    return requestJSON(url.href, {method, headers: {Authorization: `Bearer ${useFallback ? token : init.direct.token}`,
      ...(body ? {'Content-Type': 'application/octet-stream'} : {})}, body}, signal);
  };
  async function status() {
    const result = await retry(() => transfer('status', 'GET'), signal, onRetry);
    offset = validateOffset(result.received_bytes ?? 0); report();
  }
  async function fallback() {
    if (!init.fallback) return false;
    useFallback = true;
    onRetry?.({message: 'Сохраняем через основной сервер. Перенос в хранилище выполнится автоматически.', transport: 'main-fallback'});
    // The server switches this identity to a durable spool; remote partial data
    // is not treated as a local acknowledgement. A new local copy starts at 0.
    await status();
    return true;
  }
  await renew(prepared);
  if (committed) {
    onProgress?.({loaded: file.size, total: file.size, percent: 100, transport: init.transport});
    return {...init, status: 'ok', upload_id: uploadId};
  }
  if (nearExpiry()) await renew();
  if (useFallback) await fallback();
  let failures = 0, chunkSize = 256 * 1024;
  async function sendChunks() {
    while (offset < file.size) {
      checkSignal(signal);
      if (nearExpiry()) await renew();
      if (offset >= file.size) break;
      try {
        const before = offset, started = Date.now();
        const descriptor = useFallback ? init.fallback : init.direct;
        const configured = descriptor.chunk_size;
        const maximum = Number.isSafeInteger(configured) && configured > 0 ? Math.min(configured, 2 * 1024 * 1024) : 512 * 1024;
        const size = Math.min(chunkSize, maximum);
        const result = await transfer('chunk', 'PUT', file.slice(offset, Math.min(file.size, offset + size)));
        offset = validateOffset(result.received_bytes);
        if (offset <= before) throw error('Хранилище не подтвердило следующую часть.');
        failures = 0;
        if (Date.now() - started < 5000) chunkSize = Math.min(2 * 1024 * 1024, chunkSize * 2);
        report();
      } catch (failure) {
        checkSignal(signal);
        if (!useFallback && failure.status === 507 && await fallback()) { failures = 0; continue; }
        if ((!retryable(failure.status) && ![401, 409, 413].includes(failure.status)) || failures++ >= 6) throw failure;
        if (!useFallback && retryable(failure.status) && failures >= 2 && await fallback()) { failures = 0; continue; }
        chunkSize = Math.max(32 * 1024, Math.floor(chunkSize / 2));
        onRetry?.({attempt: failures, message: 'Продолжаем с подтверждённого места…', transport: useFallback ? 'main-fallback' : 'direct'});
        await pause(Math.min(1000 * 2 ** (failures - 1), 10000), signal);
        if (useFallback) await status(); else await renew();
      }
    }
  }
  async function finishTransfer() {
    for (let attempt = 0; ; attempt++) {
      await sendChunks();
      checkSignal(signal);
      if (nearExpiry()) await renew();
      if (offset < file.size) continue;
      if (committed) return;
      try { await transfer('finish', 'POST'); break; }
      catch (failure) {
        checkSignal(signal);
        if (!useFallback && failure.status === 507 && await fallback()) { failures = 0; continue; }
        if (attempt >= 6 || (!retryable(failure.status) && failure.status !== 401)) throw failure;
        if (!useFallback && retryable(failure.status) && attempt >= 1 && await fallback()) { failures = 0; continue; }
        onRetry?.({attempt: attempt + 1, message: 'Подтверждаем сохранение файла…', transport: useFallback ? 'main-fallback' : 'direct'});
        await pause(Math.min(1000 * 2 ** attempt, 10000), signal);
        if (useFallback) await status(); else await renew();
      }
    }
  }
  await finishTransfer();
  const path = typeof completePath === 'function' ? completePath(uploadId) : completePath;
  let result;
  for (let attempt = 0; ; attempt++) {
    checkSignal(signal);
    try { result = committed ? init : await control(path, {}); break; }
    catch (failure) {
      checkSignal(signal);
      if (attempt >= 6 || !retryable(failure.status)) throw failure;
      // A home finish acknowledgement does not guarantee the main server can
      // verify it later. Keep the File and same upload ID until catalog commit.
      if (!useFallback && attempt >= 1 && await fallback()) {
        failures = 0;
        await finishTransfer();
        continue;
      }
      onRetry?.({attempt: attempt + 1, message: 'Подтверждаем регистрацию файла…', transport: useFallback ? 'main-fallback' : 'direct'});
      await pause(Math.min(1000 * 2 ** attempt, 10000), signal);
    }
  }
  onProgress?.({loaded: file.size, total: file.size, percent: 100, transport: useFallback ? 'main-fallback' : 'direct'});
  return {...result, status: 'ok', upload_id: uploadId};
}
